// FILE: src/features/nmck-calculator/actions/calculate.ts
// VERSION: 1.1.0
// START_MODULE_CONTRACT:
// PURPOSE: Server action расчёта НМЦК (RPC calculate_nmtck) и тип params для персиста/шаринга.
// SCOPE: Чистые типы + server action; params уходит в details.params nmtck_calculations.
// INPUT: CalculateNmtckParams (вкл. опциональные start_date/end_date для label ГБР на share/PDF).
// OUTPUT: CalculateNmtckResponse.
// KEYWORDS: DOMAIN(NMCC): Calculation; TECH(Next): ServerAction
// END_MODULE_CONTRACT
//
// START_CHANGE_SUMMARY:
// LAST_CHANGE: [1.1.0] — Добавлены опциональные start_date?/end_date? (ISO): точный label срока ГБР
//   на /calc/s/[id] и в PDF через contractDuration(даты); fallback старых расчётов —
//   gbrDurationFromBillable(gbr_months). Даты НЕ участвуют в почасовом расчёте НМЦК.
// END_CHANGE_SUMMARY

"use server";

import { createClient } from "@oxpa/shared/lib/supabase/server";

export interface CalculateNmtckParams {
  mrot_value: number;
  regional_coef: number;
  north_coef: number;
  snr_hours: number;
  
  total_hours: number;
  night_hours: number;
  holiday_hours: number;
  
  posts_count: number;
  schedule_hours: number;
  
  insurance_rate: number;
  indirect_rate: number;
  profit_rate: number;
  
  stso_amount: number;
  insurance_amount: number;
  
  u_special_eq: boolean;
  u_armed: boolean;
  u_mass_events: boolean;
  u_antiterror: boolean;
  u_state_secret: boolean;
  
  inflation_index: number;
  vat_percent: number;
  
  title?: string;

  // Даты контракта (ISO-строки) — для точного label срока ГБР на share/PDF (contractDuration),
  // НЕ участвуют в почасовом расчёте НМЦК (там RPC по hours). У старых расчётов отсутствуют.
  start_date?: string;
  end_date?: string;

  // Пультовая охрана с ГБР (рыночная опция, вне Приказа №45) — персист/шаринг details.params
  gbr_enabled?: boolean;
  gbr_object_type?: string;
  gbr_fee_monthly?: number;
  gbr_months?: number;
  gbr_armed?: boolean;
}

export interface CalculateNmtckResponse {
  total_hours: number;
  base_hour_rate: number;
  adjusted_hour_rate: number;
  night_addition_total: number;
  holiday_addition_total: number;
  vacation_reserve_total: number;
  fot_total: number;
  sv_total: number;
  direct_cost_total: number;
  indirect_cost_total: number;
  profit_total: number;
  posts_count: number;
  total_u_coef: number;
  svohr_total: number;
  vat_amount: number;
  nmtck_total: number;
}

export async function calculateNmtckAction(params: CalculateNmtckParams) {
  const supabase = await createClient();

  // Отправляем все параметры в базу
  const { data, error } = await supabase.rpc("calculate_nmtck", {
    params: params as any,
  });

  if (error) {
    console.error("calculate_nmtck error:", error);
    throw new Error(error.message);
  }

  // Проверяем наличие сессии; если нет (открытый SEO лендинг), то не сохраняем историю
  const { data: { session } } = await supabase.auth.getSession();

  if (session?.user) {
    const { error: insertError } = await supabase
      .from("nmtck_calculations")
      .insert({
        user_id: session.user.id,
        title: params.title || "Новый расчет",
        mrot_value: params.mrot_value,
        regional_coef: params.regional_coef,
        north_coef: params.north_coef,
        posts_count: params.posts_count,
        schedule_hours: params.schedule_hours,
        is_armed: params.u_armed, // backwards compatibility in the main column
        inflation_index: params.inflation_index,
        vat_percent: params.vat_percent,
        calculated_price: (data as unknown as CalculateNmtckResponse).nmtck_total,
        details: { params, ...(data as any) }, // сохраним все новые параметры
      });

    if (insertError) {
      console.error("Failed to save calculation history:", insertError);
    }
  }

  return data as unknown as CalculateNmtckResponse;
}
